import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Phone, Smartphone, Cake, Search } from "lucide-react";
import { toast } from "sonner";
import { api, POSITIONS, initials, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", position: "Registered Nurse", employee_id_number: "", vitas_phone: "", personal_phone: "", birthday: "", email: "", pto_balance: 0, notes: "" };

export function EmployeeForm({ initial, onSaved, open, onOpenChange, employeeId }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial || EMPTY); }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form, pto_balance: parseFloat(form.pto_balance) || 0 };
      if (employeeId) await api.put(`/employees/${employeeId}`, payload);
      else await api.post("/employees", payload);
      toast.success(employeeId ? "Employee updated" : "Employee added");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Failed to save employee");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg sm:max-w-lg" data-testid="employee-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{employeeId ? "Edit Employee" : "Add Employee"}</DialogTitle><DialogDescription>Enter the employee's contact details and PTO balance.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="emp-name">Full Name</Label>
            <Input id="emp-name" value={form.name} onChange={set("name")} className="mt-1.5" data-testid="emp-name-input" />
          </div>
          <div>
            <Label htmlFor="emp-id-number">Employee ID Number</Label>
            <Input id="emp-id-number" value={form.employee_id_number} onChange={set("employee_id_number")} className="mt-1.5" placeholder="e.g. VITAS-1042" data-testid="emp-id-number-input" />
          </div>
          <div>
            <Label>Position</Label>
            <Select value={form.position} onValueChange={set("position")}>
              <SelectTrigger className="mt-1.5" data-testid="emp-position-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {POSITIONS.map((p) => <SelectItem key={p} value={p} data-testid={`position-${p.replace(/\s+/g, "-").toLowerCase()}`}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="emp-bday">Birthday</Label>
            <Input id="emp-bday" type="date" value={form.birthday} onChange={set("birthday")} className="mt-1.5" data-testid="emp-birthday-input" />
          </div>
          <div>
            <Label htmlFor="emp-vitas">VITAS Cell Phone</Label>
            <Input id="emp-vitas" value={form.vitas_phone} onChange={set("vitas_phone")} className="mt-1.5" data-testid="emp-vitas-input" />
          </div>
          <div>
            <Label htmlFor="emp-personal">Personal Phone</Label>
            <Input id="emp-personal" value={form.personal_phone} onChange={set("personal_phone")} className="mt-1.5" data-testid="emp-personal-input" />
          </div>
          <div>
            <Label htmlFor="emp-email">Email</Label>
            <Input id="emp-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="emp-email-input" />
          </div>
          <div>
            <Label htmlFor="emp-pto">PTO Balance (hours)</Label>
            <Input id="emp-pto" type="number" step="0.5" value={form.pto_balance} onChange={set("pto_balance")} className="mt-1.5" data-testid="emp-pto-input" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="emp-notes">Notes</Label>
            <Input id="emp-notes" value={form.notes} onChange={set("notes")} className="mt-1.5" data-testid="emp-notes-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-md" onClick={() => onOpenChange(false)} data-testid="emp-cancel-btn">Cancel</Button>
          <Button className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={save} disabled={saving} data-testid="emp-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => toast.error("Failed to load employees"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = employees.filter((e) =>
    e.name.toLowerCase().includes(search.toLowerCase()) || e.position.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Employee Directory</h1>
          <p className="mt-1 text-sm text-slate-500">{employees.length} team members</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search staff…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56 rounded-md pl-9" data-testid="employee-search" />
        </div>
        <Button className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={() => setShowForm(true)} data-testid="add-employee-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Employee
        </Button>
      </div>

      {filtered.length === 0 && (
        <Card className="rounded-lg border-dashed border-slate-300" data-testid="employees-empty">
          <CardContent className="py-16 text-center">
            <p className="text-sm text-slate-500">No employees yet. Add your nurses, aides, social worker, chaplain and secretary to get started.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((emp) => (
          <Link key={emp.id} to={`/employees/${emp.id}`} data-testid={`employee-card-${emp.id}`}>
            <Card className="h-full rounded-lg border-slate-200 transition-all duration-150 hover:border-slate-300 hover:shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-3.5">
                  <Avatar className="h-12 w-12"><AvatarImage src={photoUrl(emp)} alt={emp.name} /><AvatarFallback className="bg-[#2C5545] text-sm font-semibold text-white">{initials(emp.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-base font-semibold text-slate-900">{emp.name}</p>
                    <Badge variant="outline" className="mt-1 border-slate-200 text-xs text-slate-600">{emp.position}</Badge>
                    {emp.employee_id_number && <p className="mt-1 text-xs font-medium text-[#5C6661]" data-testid={`emp-id-${emp.id}`}>ID: {emp.employee_id_number}</p>}
                  </div>
                  <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600" data-testid={`pto-${emp.id}`}>{emp.pto_balance}h PTO</span>
                </div>
                <div className="mt-4 space-y-1.5 text-xs text-slate-500">
                  {emp.vitas_phone && <p className="flex items-center gap-2"><Smartphone className="h-3.5 w-3.5" /> VITAS: {emp.vitas_phone}</p>}
                  {emp.personal_phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {emp.personal_phone}</p>}
                  {emp.birthday && <p className="flex items-center gap-2"><Cake className="h-3.5 w-3.5" /> {emp.birthday}</p>}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <EmployeeForm open={showForm} onOpenChange={setShowForm} onSaved={load} />
    </div>
  );
}
